import { t } from "i18next";
import { useState } from "react";
import { FormState, SubmitErrorHandler, SubmitHandler, UseFormSetError } from "react-hook-form";
import { convertFormToInsertInvoice } from "@/utils/invoiceUtils";
import { BankAccountDto, InvoiceFormData, InsertInvoiceDto } from "@/types";

type UseSubmitProps = {
    totalAmount: number;
    bankAccounts: BankAccountDto[];
    invoiceId: string;
    clientVatNumber?: string;
    formState: FormState<InvoiceFormData>;
    setError: UseFormSetError<InvoiceFormData>;
    updateInvoice: (props: { id: string; invoice: InsertInvoiceDto }) => Promise<string>;
    submitInvoice: (props: { id: string; invoiceDetails: InsertInvoiceDto }) => Promise<unknown>;
    setMissingBankAccount: (value: string | undefined) => void;
    resetFormChanged: () => void;
    getValues: () => InvoiceFormData;
};

export const useSubmit = ({
    totalAmount,
    bankAccounts,
    invoiceId,
    clientVatNumber,
    formState,
    setError,
    updateInvoice,
    submitInvoice,
    setMissingBankAccount,
    resetFormChanged,
    getValues
}: UseSubmitProps) => {
    const [submitError, setSubmitError] = useState("");

    const onSubmit: SubmitHandler<InvoiceFormData> = async _ => {
        const data = getValues();
        const invoiceDateStr = normalizeDate(data.invoiceDate);

        setMissingBankAccount(undefined);

        const validationHits: string[] = [];

        validationHits.push(
            ...validateBankAccount(data, setError, setMissingBankAccount),
            ...validateInvoiceDatePast(invoiceDateStr, setError, setSubmitError),
            ...validateTotalAmount(totalAmount, setSubmitError),
            ...validateAttachment(data, setError),
            ...validateInvoiceDateFormat(invoiceDateStr, setError),
            ...validateVat(data, setError, clientVatNumber),
            ...validateClaimReferences(data, setError)
        );

        if (validationHits.length > 0) {
            return;
        }

        setSubmitError("");

        const bankAccount = bankAccounts.find(
            e => e.accountNumber === data.bankAccount?.accountNumber && e.currency === data.bankAccount?.currency
        );

        const invoiceDetails: InsertInvoiceDto = convertFormToInsertInvoice(data, bankAccount);

        await updateInvoice({
            id: invoiceId,
            invoice: invoiceDetails
        });
        resetFormChanged();

        await submitInvoice({
            id: invoiceId,
            invoiceDetails
        });
    };

    const onErrorSubmit: SubmitErrorHandler<InvoiceFormData> = _ => {
        const data = getValues();

        if (!formState.isValid) {
            const amountOfErrors = Object.keys(formState.errors).length;
            setSubmitError(
                amountOfErrors === 0
                    ? ""
                    : t("errorMessages:invoiceForm.errorAmount", {
                          amount: amountOfErrors
                      })
            );
        }

        if (totalAmount === 0) {
            setSubmitError(t("errorMessages:invoiceForm.missingCost"));
        }

        if (!isBankAccountComplete(data.bankAccount)) {
            const message = t("errorMessages:invoiceForm.missingBankAccount");
            setMissingBankAccount(message);
            setError("bankAccount", {
                message,
                type: "custom"
            });
        }

        if (!isAttachmentReady(data.invoiceAttachment)) {
            setError("invoiceAttachment", {
                message: t("errorMessages:invoiceForm.missingInvoiceFiles"),
                type: "custom"
            });
        }
    };

    return {
        onSubmit,
        onErrorSubmit,
        submitError,
        setSubmitError
    };
};

function isBankAccountComplete(bankAccount?: BankAccountDto): boolean {
    if (!bankAccount) {
        return false;
    }
    const requiredFields: (keyof BankAccountDto)[] = ["accountNumber", "currency"];
    return requiredFields.every(field => typeof bankAccount[field] === "string" && bankAccount[field].trim() !== "");
}

function normalizeDate(value: unknown): string | undefined {
    if (!value) {
        return undefined;
    }

    if (typeof value === "string") {
        return value;
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value.toISOString().split("T")[0];
    }

    return undefined;
}

function validateBankAccount(
    data: InvoiceFormData,
    setError: UseFormSetError<InvoiceFormData>,
    setMissingBankAccount: (value: string | undefined) => void
): string[] {
    if (isBankAccountComplete(data.bankAccount)) {
        return [];
    }

    const message = t("errorMessages:invoiceForm.missingBankAccount");
    setMissingBankAccount(message);
    setError("bankAccount", {
        message,
        type: "custom"
    });
    return ["missing or incomplete bankAccount"];
}

function validateInvoiceDatePast(
    invoiceDateStr: string | undefined,
    setError: UseFormSetError<InvoiceFormData>,
    setSubmitError: (value: string) => void
): string[] {
    if (!invoiceDateStr) {
        return [];
    }

    const today = new Date();
    const lastYear = new Date(
        today.getFullYear(),
        today.getMonth(),
        // eslint-disable-next-line no-magic-numbers
        today.getDate() - 366
    );
    const lastYearStr = lastYear.toISOString().split("T")[0];

    if (new Date(invoiceDateStr).getTime() > new Date(lastYearStr).getTime()) {
        return [];
    }

    const pastDateMessage = t("errorMessages:invoiceForm.pastDate");
    setSubmitError(pastDateMessage);
    setError("invoiceDate", {
        message: pastDateMessage,
        type: "custom"
    });
    return ["invoiceDate is too far in the past"];
}

function validateTotalAmount(totalAmount: number, setSubmitError: (value: string) => void): string[] {
    if (totalAmount !== 0) {
        return [];
    }

    setSubmitError(t("errorMessages:invoiceForm.missingCost"));
    return ["totalAmount is 0"];
}

function validateAttachment(data: InvoiceFormData, setError: UseFormSetError<InvoiceFormData>): string[] {
    if (isAttachmentReady(data.invoiceAttachment)) {
        return [];
    }

    setError("invoiceAttachment", {
        message: t("errorMessages:invoiceForm.missingInvoiceFiles"),
        type: "custom"
    });
    return ["missing invoiceAttachment"];
}

const dateRegex = /^(19|20)\d{2}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

function validateInvoiceDateFormat(
    invoiceDateStr: string | undefined,
    setError: UseFormSetError<InvoiceFormData>
): string[] {
    if (!invoiceDateStr || dateRegex.exec(invoiceDateStr)) {
        return [];
    }

    setError("invoiceDate", {
        message: t("errorMessages:invoiceForm.invalidDate"),
        type: "custom"
    });

    return ["invoiceDate.invalidFormat"];
}

function validateVat(
    data: InvoiceFormData,
    setError: UseFormSetError<InvoiceFormData>,
    recipientVat?: string
): string[] {
    const vatFieldValid = (value: string | undefined) => value === undefined || value === "0";

    if ((recipientVat && recipientVat !== "") || vatFieldValid(data.totalVatAmount)) {
        return [];
    }

    setError("totalVatAmount", {
        message: t("errorMessages:invoiceForm.invalidRecipient"),
        type: "custom"
    });
    return ["invalidRecipient / totalVatAmount"];
}

function isAttachmentReady(attachment?: InvoiceFormData["invoiceAttachment"]): boolean {
    return Boolean(attachment?.isUploaded && attachment?.docId && !attachment?.uploadFailed);
}

function validateClaimReferences(data: InvoiceFormData, setError: UseFormSetError<InvoiceFormData>): string[] {
    const hasClaimRef = data.claimRef && data.claimRef.trim() !== "";
    const hasSubClaimsRef = data.subClaimsRef && data.subClaimsRef.trim() !== "";

    if (hasClaimRef && hasSubClaimsRef) {
        return [];
    }

    const message = t("errorMessages:invoiceForm.missingClaimReference");
    setError("claimRef", {
        message,
        type: "custom"
    });
    setError("subClaimsRef", {
        message,
        type: "custom"
    });
    return ["claimRef / subClaimsRef"];
}
